"use client";

import React, { useEffect } from "react";
import { Stat, StatsProps } from "../types";
import Icon from "@/components/icons";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import Tooltip from "@/components/Tooltip";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { useInView } from "react-intersection-observer";
import { backgroundColors, textColors, dividerColors } from "../colors";
import { text } from "stream/consumers";
import EventLink from "@/components/Link/EventLink/EventLink";

const tooltipAllowedTags = ["b", "strong", "em", "i", "a", "p", "span"];

const Default = ({
  stats,
  background = "putty",
  trackEvents = false,
  maxStats = 4,
  header,
  subheader,
  linkText,
  linkUrl,
  htmlId,
  location,
}: StatsProps) => {
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      text: "School Stats",
      htmlId: htmlId || "stats",
      name: "stats",
      location: location || "degree-detail-page",
      position: "1",
    },
  });

  const { ref: contentRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (trackEvents && inView) {
      elementViewed();
    }
  }, [trackEvents, inView, elementViewed]);

  const backgroundColorClass = backgroundColors[background];
  const dividerColor = dividerColors[background];
  const textColor = textColors[background];

  return (
    <div className={`${backgroundColorClass} pt-20 px-4 mx-break-out`}>
      {header && (
        <div className="flex flex-col items-center mb-12">
          <h2 className={`elm-d1 italic font-black text-center ${textColor}`}>
            {header}
          </h2>
          {subheader && (
            <p
              className={`elm-p5 font-normal mt-6 px-10 md:px-20 text-center ${textColor}`}
            >
              {subheader}
            </p>
          )}
        </div>
      )}
      <div
        ref={contentRef}
        className={`container w-full gap-6 lg:gap-0 ${textColor} ${backgroundColorClass} md:my-10 ${
          maxStats > 3
            ? `grid grid-cols-2 md:grid-cols-3 lg:[grid-template-columns:1fr_auto_1fr_auto_1fr_auto_1fr]`
            : "flex flex-col md:flex-row lg:justify-around lg:items-start"
        }`}
      >
        {stats.map((stat: Stat, index: number) => (
          <React.Fragment key={`${stat.label}-${index}`}>
            <div
              className={`flex flex-col ${index >= maxStats ? "md:hidden" : ""}`}
            >
              <div className={`flex flex-col items-center`}>
                <div
                  className={`flex ${stat.icon ? "items-start" : "items-center px-2"}`}
                >
                  {stat.icon && (
                    <div className="flex-shrink-0">
                      <Icon
                        type={stat.icon}
                        svgProps={{ width: 56, height: 56 }}
                      />
                    </div>
                  )}
                  <div className="flex flex-col items-center">
                    <div
                      className={`mb-2 text-4xl font-black italic leading-none ${stat.icon ? "text-left" : "text-center"}`}
                    >
                      {stat.value}
                    </div>
                    <div className="text-center px-4">
                      {sanitizeAndParseInlineHTML(
                        stat.label,
                        background === "elm",
                      )}
                      {stat.tooltip && (
                        <Tooltip
                          content={sanitizeAndParseInlineHTML(
                            stat.tooltip,
                            false,
                            tooltipAllowedTags,
                          )}
                          className="text-xs ml-[3px]"
                        >
                          <Icon
                            type="outline-warning-circle"
                            svgProps={{ width: 20, height: 20 }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider as a separate flex item */}
            {index < stats.length - 1 && (
              <div
                className={`justify-self-center hidden md:block min-w-[1px] w-[1px] ${dividerColor} ${maxStats > 3 && index % 2 === 1 ? "hidden md:hidden lg:block" : ""}`}
              ></div>
            )}
          </React.Fragment>
        ))}
      </div>
      {linkText && linkUrl && (
        <div className="flex justify-center text-center mt-6">
          <EventLink
            href={linkUrl}
            text={linkText}
            class_name={`underline underline-offset-4 ${textColor === "text-white" ? "text-white" : "text-primary-500"} cursor-pointer hover:text-primary-500 text-base font-medium hover:no-underline hover:font-bold`}
            location="homepage"
            html_id="stats-link"
            name="stats-link"
            element_type={ElementType.Link}
            position="1"
          />
        </div>
      )}
    </div>
  );
};

export default Default;
